import lodash from 'lodash'
import store from '@/store'
import { contactTypes } from '@/api/users/consts'
import { getAccountById } from '@/api/accounts/request'
import { getBuildingInfoById } from '@/api/buildings/request'
import { getLastHistoryByStatusWithoutResume } from '@/api/workPermit/util'
import { tenantProjectRouteNames, projectRouteNames, envParameterNames } from '@/utils/consts'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS, WORK_PERMIT_EXTENDED } from '@/modules/workPermits/consts'
import i18n from '@/utils/i18n'
import dateUtil from '@/utils/dateUtil'
import { fullYearTimeFormat } from '@/utils/regex'
import filters from '@/filters/filters'
import * as regex from '@/utils/regex'
import { contractorPermsTypes } from '@/utils/aclHelper'
import { getRegionalWorkPermitLabels } from '@/modules/workPermits/regionalWorkPermitLabels'
import { getVendorDetailByIdAndSite } from '../users/request'
import { getLinkToTab, getVendorSubEmailsByRight } from './util'

function getActionMap () {
  return {
    WAPPR: i18n.t('workPermit.actionType.submitted'),
    ACKNOWLEDGED: i18n.t('workPermit.actionType.acknowledged'),
    REVIEWED: i18n.t('workPermit.actionType.reviewed'),
    REVIEWED2: i18n.t('workPermit.actionType.reviewed'),
    APPR: i18n.t('workPermit.actionType.submitted'),
  }
}

export function getObjectValue (object, propertyString) {
  const ret = lodash.get(object, propertyString, '--') || '--'
  return Array.isArray(ret) ? ret.join(',') : ret
}

export async function getLink (permit, routeName = tenantProjectRouteNames.tenantProject, query = {}, hostname = envParameterNames.engineerEntry) {
  const hostName = store.getters[`settings/${hostname}`] || location.origin

  if (permit.standalone) {
    let params = { id: permit.id }
    if (permit.type === WORK_PERMIT_TYPES.HOISTING) {
      query.hoistingId = permit.id
      params.id = permit.parentId
    }
    return getLinkToTab(routeName, params, query, hostName)
  }

  const defaultQuery = {
    tabIndex: 3,
    showDetail: true,
    wpId: permit.id,
    from: tenantProjectRouteNames.tenantProjectDetail,
  }
  if (!permit.isTenantProject) {
    defaultQuery.from = projectRouteNames.ProjectDetail
  }
  if (permit.type === WORK_PERMIT_TYPES.HOISTING) {
    query.hoistingId = permit.id
    query.wpId = permit.parentId
  }
  return getLinkToTab(routeName, { id: permit.projectId }, Object.assign({}, defaultQuery, query), hostName)
}

export function generateDuration (startDate, endDate) {
  return `${dateUtil.format(startDate, fullYearTimeFormat)} - ${dateUtil.format(endDate, fullYearTimeFormat)}`
}

export function getPermitBuildingCode (permit) {
  return lodash.get(permit, permit.isTenantProject ? permit.standalone ? 'tenancy.building.code' : 'project.tenancy.building.code' : 'building.code')
}

export async function findStandaloneSubmitterEmail (permit) {
  let res = {
    emailTo: '',
    cc: [],
  }
  const creatorHistory = getLastHistoryByStatusWithoutResume(permit.histories, WORK_PERMIT_STATUS.WAPPR)
  const creatorId = creatorHistory.who.id

  if (permit.isTenantProject) {
    res = findTMSStandaloneSubmitterEmail(permit, creatorId)
  } else {
    res = await findCMSStandaloneSubmitterEmail(permit, creatorId)
  }
  return res
}

export async function findSubmitterEmail (permit) {
  if (permit.standalone) {
    const { emailTo } = await findStandaloneSubmitterEmail(permit)
    return emailTo
  }
  const creatorAsVendor = findCreatorAsVendorEmail(permit)
  if (creatorAsVendor) {
    return creatorAsVendor
  } else {
    const creatorContractor = getSubmitterProjectContractor(permit)
    if (creatorContractor && creatorContractor.primaryContact && creatorContractor.primaryContact.active) {
      return creatorContractor.primaryContact.email || getSameCompanyContractors(permit)
        .map(contractor => contractor.primaryContact && contractor.primaryContact.active && contractor.primaryContact.email)
        .filter(email => email)
    }
  }
}

export function getRelatedVendorEmail (histories) {
  let submitterEmails = []
  histories.forEach(history => {
    if (history.status === WORK_PERMIT_STATUS.WAPPR && !history.detail.isReusme) {
      const email = getCreatorAsVendorEmail(history)
      if (email) {
        submitterEmails.push(email)
      }
      const currentMainContractor = store.getters['projectContractors/currentMainContractor']
      if (Object.keys(currentMainContractor).length && currentMainContractor.primaryContact.userId === history.who.id) {
        submitterEmails.push(currentMainContractor.primaryContact.email)
      }
    }
  })
  return submitterEmails
}

function findTMSStandaloneSubmitterEmail (permit, creatorId) {
  let res = {
    emailTo: '',
    cc: [],
  }
  if (!(permit.tenancy)) return res

  const { accounts } = permit.tenancy
  let tenancyAccountAsCreator = accounts.find(item => item.id === creatorId)
  if (tenancyAccountAsCreator) {
    res.emailTo = tenancyAccountAsCreator.username
  } else {
    const contractorCreator = permit.tenancy.tenancyContractors.filter(item => item.userId === creatorId)[0]
    res.emailTo = contractorCreator.user.username
  }
  accounts.map(item => item.TenancyAccount.perms.StandaloneWorkPermits && res.cc.push(`${item.username}`))
  return res
}

async function findCMSStandaloneSubmitterEmail (permit, creatorId) {
  let res = {
    emailTo: '',
    cc: [],
  }
  const { users } = await getAccountById(permit.contractor.id)
  const creatorInfo = lodash.filter(users, user => user.id === creatorId)[0] || {}

  let creator = ''
  if (creatorInfo.enabled) {
    const contact = creatorInfo.contacts.find(contact => contact.type === contactTypes.EMAIL)
    creator = lodash.get(contact, 'value', creatorInfo.username)
  }

  // Use PHYSICAL site code to get vendor info and sub accounts
  const vendorId = permit.contractorId
  const site = await store.dispatch('buildings/getBuildingPhysicalSite', permit.buildingId)
  const [vendorDetail, vendorSubAccounts] = await getVendorDetailByIdAndSite(vendorId, site.code)

  let vendorAdminContact = ''
  if (vendorDetail && vendorDetail.email && vendorDetail.enabled) {
    vendorAdminContact = vendorDetail.email || vendorDetail.username
  }
  res.emailTo = creator || vendorAdminContact

  const subAccountEmails = getVendorSubEmailsByRight(vendorSubAccounts, contractorPermsTypes.standaloneWorkPermits.readUpdate)
  res.cc = subAccountEmails

  return res
}

function findCreatorAsVendorEmail (permit) {
  let result
  if (!permit.isTenantProject) {
    const creatorHistory = getLastHistoryByStatusWithoutResume(permit.histories, WORK_PERMIT_STATUS.WAPPR)
    result = getCreatorAsVendorEmail(creatorHistory)
  }
  return result
}

function getCreatorAsVendorEmail (history) {
  let contactEmail
  const currentVendors = store.getters['projectContractors/currentVendorUsers'] || []
  const creatorAsVendor = currentVendors.find(vendor => vendor.id === history.who.id)
  if (creatorAsVendor && creatorAsVendor.contacts.length) {
    contactEmail = creatorAsVendor.contacts.filter(contact => contact.type === contactTypes.EMAIL)[0]
  }
  return contactEmail ? contactEmail.value : null
}

function getSubmitterProjectContractor (permit) {
  const projectContractorId = getSubmitterProjectContractorId(permit)
  const contractors = getSameCompanyContractors(permit)
  return contractors.find(contractor => contractor.id === projectContractorId)
}

function getSameCompanyContractors (permit) {
  const currentMainContractor = store.getters['projectContractors/currentMainContractor']
  const currentContractors = store.getters['projectContractors/currentContractors']
  return [currentMainContractor, ...currentContractors].filter(c => c.companyId === permit.contractorId)
}

function getSubmitterProjectContractorId (permit) {
  const wapprHistory = getLastHistoryByStatusWithoutResume(permit.histories, WORK_PERMIT_STATUS.WAPPR)
  return wapprHistory.who.projectContractorId
}

export function getSubmitter (permit) {
  const history = permit.histories && permit.histories.find(item => item.status === WORK_PERMIT_STATUS.DRAFT)
  return (history && history.who) || {}
}

export function findMainContractorEmail () {
  const mainContractor = store.getters['projectContractors/currentMainContractor']
  if (mainContractor && mainContractor.primaryContact && mainContractor.primaryContact.active) {
    return mainContractor.primaryContact.email
  }
}

function formatDateAndStatus (permit) {
  permit.duration = generateDuration(permit.startDate, permit.endDate)
  permit.formatStatus = filters.customStatusLabel(permit.status)
  if (permit.hoistings && permit.hoistings.length) {
    permit.hoistings.forEach(hoisting => {
      hoisting.formatStatus = filters.customStatusLabel(hoisting.status)
      hoisting.duration = generateDuration(hoisting.startDate, hoisting.endDate)
    })
  }
}

export async function buildMetaData (permit) {
  permit = lodash.cloneDeep(permit)
  const wapprHistory = getLastHistoryByStatusWithoutResume(permit.histories, WORK_PERMIT_STATUS.WAPPR)
  const lastHistory = getLastHistoryByStatusWithoutResume(permit.histories, permit.status)
  if (permit.hoistings && permit.hoistings.length) {
    permit.hoistings = permit.hoistings.filter(hoisting => hoisting.status !== WORK_PERMIT_STATUS.DRAFT)
  }
  const actionMap = getActionMap()
  const lastHistoryUsername = lodash.get(lastHistory, 'who.username', '')
  formatDateAndStatus(permit)
  let metaData = {
    code: permit.code,
    contractor: {
      id: permit.contractor.id,
      company: permit.contractor.name,
      name: getObjectValue(wapprHistory, 'who.displayName'),
      email: getObjectValue(wapprHistory, 'who.username'),
    },
    subsidiary: i18n.t('system.title'),
    isHoisting: false,
    workPermitLabels: getRegionalWorkPermitLabels(),
    permit,
    action: actionMap[permit.status],
    actioner: lodash.get(lastHistory, 'who.displayName', ''),
    actionerEmail: regex.email.test(lastHistoryUsername) ? lastHistoryUsername : '',
  }
  let building
  let tenancy = { type: null }

  if (permit.standalone && !permit.isTenantProject) {
    building = permit.building
  } else {
    if (permit.isTenantProject) {
      const currentTenantProject = permit.project
      tenancy = permit.standalone ? permit.tenancy : currentTenantProject.tenancy
      building = await getBuildingInfoById(tenancy.buildingId)

      metaData = {
        ...metaData,
        tenant: {
          company: deep(tenancy, 'company.name'),
        },
        premises: permit.standalone ? i18n.t('emptyValue') : getObjectValue(currentTenantProject, 'projectLocation'),
      }
    } else {
      building = permit.building
      const currentProject = store.getters['projects/currentProject']
      const mainContractor = currentProject.mainContractor
      if (permit.contractorId === mainContractor.companyId) {
        permit.isMainContractorWP = true
      }
      const purchase = store.getters['projects/purchase']
      metaData = {
        ...metaData,
        projectName: deep(purchase, 'description'),
        premises: permit.location,
        site: deep(purchase, 'siteId'),
      }
    }
  }
  metaData.building = building.fullName

  if (permit.type === WORK_PERMIT_TYPES.HOISTING) {
    metaData.isHoisting = true
  }
  return { metaData, building, tenancy }
}

export function buildExtendMetaDate (permit, metaData) {
  let result = lodash.cloneDeep(metaData)
  result.uppercaseBuiliding = permit.project.tenancy.building
  result.duration = generateDuration(permit.startDate, permit.requestExtendDate)
  result.originEndDate = dateUtil.format(permit.extendDate || permit.endDate, fullYearTimeFormat)
  return result
}

export async function amendRejectExtensionMetaDate (permit, metaData) {
  // omit unused data in order to reduce the request payload
  let result = lodash.omit(metaData, ['permit.histories', 'permit.hoistings', 'permit.workers'])
  const sortedHistories = lodash.orderBy(permit.histories, ['createdAt'], ['desc'])
  const rejectedHistory = sortedHistories.find(item => {
    const isRelatedExtension = [
      lodash.get(item, 'detail.comments'),
      lodash.get(item, 'detail.externalComments')]
      .some(comment => String(comment || '').trim() === WORK_PERMIT_EXTENDED)
    const rejectReason = String(lodash.get(item, 'detail.rejectReason') || '').trim()
    if (isRelatedExtension && rejectReason) return item
  })
  const requestExtendDate = lodash.get(rejectedHistory, 'detail.requestExtendDate', i18n.t('emptyValue'))
  result.duration = generateDuration(permit.startDate, requestExtendDate)
  result.originEndDate = dateUtil.format(permit.extendDate || permit.endDate, fullYearTimeFormat) || i18n.t('emptyValue')
  result.reason = lodash.get(rejectedHistory, 'detail.rejectReason', i18n.t('emptyValue'))
  return result
}
